import React, { useState, useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { DialogFixUtil } from '@/components/ui/dialog-fix-util';
import { Button } from '@/components/ui/button';
import { FileUp, Loader2, X, AlertCircle, CheckCircle, RotateCw, Clock } from 'lucide-react';
import { propertyApi } from '@/services/api';
import { CSVFileInput } from '@/components/shared/CSVFileInput';
import { useCSVUpload } from '@/hooks/use-csv-upload';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from '@/components/ui/table';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Progress } from '@/components/ui/progress';
import { ImportJobStatus, AsyncUploadResponse } from '@/types/upload';

interface AssociationPropertiesBulkUploadModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  managementCompanyId: number;
}

export const AssociationPropertiesBulkUploadModal: React.FC<AssociationPropertiesBulkUploadModalProps> = ({
  open,
  onOpenChange,
  managementCompanyId
}) => {
  const queryClient = useQueryClient();
  const [jobStatus, setJobStatus] = useState<ImportJobStatus | null>(null);
  const [pollingInterval, setPollingInterval] = useState<number | null>(null);

  const handleUploadSuccess = (result: AsyncUploadResponse) => {
    // Start polling for job status
    setJobStatus({
      jobId: result.jobId,
      status: 'pending',
      message: result.message
    });
    
    // Start polling
    const intervalId = window.setInterval(() => {
      checkJobStatus(result.jobId);
    }, 3000);
    
    setPollingInterval(intervalId);
  };

  const handleFinalSuccess = () => {
    // Refetch properties data
    queryClient.invalidateQueries({
      queryKey: ['properties']
    });
    
    // Refetch associations data
    queryClient.invalidateQueries({
      queryKey: ['associations']
    });
  };

  const checkJobStatus = async (jobId: string) => {
    try {
      const status = await propertyApi.checkImportJobStatus(jobId);
      setJobStatus(status);
      
      // If job is completed or failed, stop polling
      if (status.status === 'completed' || status.status === 'failed') {
        if (pollingInterval) {
          clearInterval(pollingInterval);
          setPollingInterval(null);
        }
        
        // If job completed successfully, update data
        if (status.status === 'completed') {
          handleFinalSuccess();
        }
      }
    } catch (error) {
      console.error("Error checking job status:", error);
    }
  };

  useEffect(() => {
    // Clean up interval on unmount
    return () => {
      if (pollingInterval) {
        clearInterval(pollingInterval);
      }
    };
  }, [pollingInterval]);

  const {
    file,
    error,
    uploading,
    importResult,
    handleFileChange,
    handleSubmit,
    handleCancel,
    resetResult,
    setError
  } = useCSVUpload<AsyncUploadResponse>({
    uploadFn: (file) => propertyApi.bulkUploadWithAssociation(file),
    onSuccess: handleUploadSuccess,
    onCancel: () => onOpenChange(false)
  });

  const handleDialogChange = (open: boolean) => {
    if (!uploading && !jobStatus?.status) {
      // Only allow closing if not currently uploading or processing
      onOpenChange(open);
    } else if (!open && (jobStatus?.status === 'completed' || jobStatus?.status === 'failed')) {
      // Allow closing if job is complete or failed
      onOpenChange(open);
      setJobStatus(null);
    }
  };

  // Handle retry after seeing errors
  const handleRetry = () => {
    resetResult();
    setJobStatus(null);
  };

  // Handle continue after seeing errors
  const handleContinue = () => {
    if (jobStatus?.status === 'completed' || jobStatus?.status === 'failed') {
      onOpenChange(false);
      setJobStatus(null);
    }
  };

  // Handle export of properties - Don't validate file for export
  const handleExportProperties = async () => {
    try {
      // Clear any error messages when exporting
      setError(null);
      
      const csvData = await propertyApi.exportCsv(managementCompanyId);
      
      // Create a blob and download it
      const blob = new Blob([csvData], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'properties_export.csv';
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (err) {
      console.error("Failed to export properties:", err);
    }
  };

  // Modify the form submission to validate file only on upload
  const handleFormSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validate file before submitting
    if (!file) {
      setError('Please select a CSV file to upload');
      return;
    }
    
    // If file is valid, proceed with submission
    handleSubmit(e);
  };

  // Render status based on job status
  const renderJobStatus = () => {
    if (!jobStatus) return null;
    
    const progress = jobStatus.status === 'completed' && jobStatus.total 
      ? Math.round((jobStatus.imported || 0) / jobStatus.total * 100) 
      : jobStatus.status === 'processing' 
        ? 50 
        : jobStatus.status === 'completed' 
          ? 100 
          : 0;
          
    return (
      <div className="space-y-4">
        <Alert variant={jobStatus.status === 'failed' ? 'destructive' : (jobStatus.status === 'completed' ? 'default' : 'default')}>
          {jobStatus.status === 'pending' && <Clock className="h-4 w-4" />}
          {jobStatus.status === 'processing' && <Loader2 className="h-4 w-4 animate-spin" />}
          {jobStatus.status === 'completed' && <CheckCircle className="h-4 w-4" />}
          {jobStatus.status === 'failed' && <AlertCircle className="h-4 w-4" />}
          
          <AlertTitle>
            {jobStatus.status === 'pending' && 'Job Queued'}
            {jobStatus.status === 'processing' && 'Processing Import'}
            {jobStatus.status === 'completed' && 'Import Completed'}
            {jobStatus.status === 'failed' && 'Import Failed'}
          </AlertTitle>
          
          <AlertDescription>
            {jobStatus.status === 'pending' && 'Your file has been uploaded and is queued for processing. This may take a few minutes.'}
            {jobStatus.status === 'processing' && 'Your file is being processed. Please wait.'}
            {jobStatus.status === 'completed' && jobStatus.imported !== undefined && (
              `Successfully imported ${jobStatus.imported} ${jobStatus.total ? `out of ${jobStatus.total}` : ''} properties.`
            )}
            {jobStatus.status === 'failed' && 'There was an error processing your file.'}
          </AlertDescription>
        </Alert>
        
        {jobStatus.status !== 'failed' && (
          <div className="space-y-2">
            <div className="flex justify-between text-sm">
              <span>Progress</span>
              <span>{progress}%</span>
            </div>
            <Progress value={progress} className="h-2" />
          </div>
        )}
        
        {jobStatus.errors && jobStatus.errors.length > 0 && (
          <div className="max-h-[350px] overflow-y-auto border rounded">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-16">#</TableHead>
                  <TableHead>Error</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {jobStatus.errors.map((error, index) => (
                  <TableRow key={index}>
                    <TableCell className="font-medium">{index + 1}</TableCell>
                    <TableCell className="text-sm text-destructive">{error}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
        
        {(jobStatus.status === 'completed' || jobStatus.status === 'failed') && (
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={handleRetry}>
              <RotateCw className="mr-2 h-4 w-4" />
              Upload Another File
            </Button>
            <Button onClick={handleContinue}>
              <CheckCircle className="mr-2 h-4 w-4" />
              {jobStatus.status === 'completed' ? 'Done' : 'Close'}
            </Button>
          </DialogFooter>
        )}
      </div>
    );
  };

  return (
    <Dialog open={open} onOpenChange={handleDialogChange}>
      <DialogFixUtil open={open} />
      <DialogContent 
        className={`overflow-hidden ${(jobStatus?.errors?.length || 0) > 0 ? 'max-w-3xl' : ''}`}
        onPointerDownOutside={(e) => {
          // Prevent closing when uploading or processing
          if (uploading || jobStatus?.status === 'processing' || jobStatus?.status === 'pending') e.preventDefault();
        }}
        onInteractOutside={(e) => {
          // Prevent closing when uploading or processing
          if (uploading || jobStatus?.status === 'processing' || jobStatus?.status === 'pending') e.preventDefault();
        }}
      >
        <DialogHeader>
          <DialogTitle>Bulk Upload Properties with Association References</DialogTitle>
          <DialogDescription>
            Upload a CSV file with property data, including the association external_id to identify which properties belong to which association.
            This process now runs asynchronously to handle large file uploads.
          </DialogDescription>
        </DialogHeader>
        
        {jobStatus ? (
          renderJobStatus()
        ) : (importResult?.errors && importResult.errors.length > 0) ? (
          <div className="space-y-4">
            <Alert>
              <AlertCircle className="h-4 w-4" />
              <AlertTitle>Import Result</AlertTitle>
              <AlertDescription>
                Successfully imported {importResult.imported} out of {importResult.total || importResult.imported + importResult.errors.length} properties.
                The following {importResult.errors.length} errors occurred:
              </AlertDescription>
            </Alert>
            
            <div className="max-h-[350px] overflow-y-auto border rounded">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-16">#</TableHead>
                    <TableHead>Error</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {importResult.errors.map((error, index) => (
                    <TableRow key={index}>
                      <TableCell className="font-medium">{index + 1}</TableCell>
                      <TableCell className="text-sm text-destructive">{error}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
            
            <DialogFooter className="gap-2">
              <Button variant="outline" onClick={handleRetry}>
                <RotateCw className="mr-2 h-4 w-4" />
                Upload Another File
              </Button>
              <Button onClick={handleContinue}>
                <CheckCircle className="mr-2 h-4 w-4" />
                Continue With Successful Imports
              </Button>
            </DialogFooter>
          </div>
        ) : (
          <form onSubmit={handleFormSubmit} encType="multipart/form-data">
            <CSVFileInput
              id="csv-file"
              error={error}
              templateUrl="/templates/properties_with_association_template.csv"
              templateFileName="properties_with_association_template.csv"
              onChange={handleFileChange}
              disabled={uploading}
              file={file}
              description="Please make sure your CSV file includes required columns: association_external_id (association reference), address, city, state, zip_code."
              showExport={true}
              onExport={handleExportProperties}
              validateOnExport={false}
              validateFileForUploadOnly={true}
            />
            
            <DialogFooter>
              <Button 
                type="button" 
                variant="outline" 
                onClick={handleCancel} 
                disabled={uploading}
              >
                Cancel
              </Button>
              <Button 
                type="submit" 
                disabled={!file || uploading}
              >
                {uploading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Uploading...
                  </>
                ) : (
                  <>
                    <FileUp className="mr-2 h-4 w-4" />
                    Upload
                  </>
                )}
              </Button>
            </DialogFooter>
          </form>
        )}

        <button
          onClick={handleCancel}
          disabled={uploading || jobStatus?.status === 'processing' || jobStatus?.status === 'pending'}
          className="absolute right-4 top-4 rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none data-[state=open]:bg-accent data-[state=open]:text-muted-foreground"
        >
          <X className="h-4 w-4" />
          <span className="sr-only">Close</span>
        </button>
      </DialogContent>
    </Dialog>
  );
};
